import {
  Button,
  Divider,
  Grid,
  Text,
  Toast,
  Icons,
} from "@pushpress/shared-components";
import React, { useContext } from "react";
import { Control, useWatch } from "react-hook-form";
import { SwitchPlanForm, getBillingIntervalType } from "./SwitchPlanForm.utils";
import { CurrentUserSubscriptionFragment } from "../SwitchPlanDrawer-generated.hooks";
import { formatToUSD } from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import SectionTitleAndDescription from "./components/SectionTitleAndDescription";
import DiscountController from "../../../components/controllers/DiscountSelect";

type SelectDiscountStepProps = {
  control: Control<SwitchPlanForm>;
  goToNextStep: VoidFunction;
  goBack: VoidFunction;
  userSubscription: CurrentUserSubscriptionFragment;
};

function SelectDiscountStep({
  control,
  goToNextStep,
  goBack,
  userSubscription,
}: SelectDiscountStepProps) {
  const { client } = useContext(AppContext);
  const newPlan = useWatch({ control: control, name: "newPlan" });
  const discount = useWatch({ control: control, name: "discount" });
  const planPrice = useWatch({ control: control, name: "planPrice" });

  const newPlanPrice = newPlan?.prices?.length && newPlan?.prices?.find(
    (price) => price.planPriceUuid === planPrice?.planPriceUuid,
  );

  const amount = planPrice ? Number(newPlanPrice?.amount) : Number(newPlan?.amount);
  const intervalType = planPrice ? newPlanPrice?.intervalType : newPlan?.intervalType;
  const interval = planPrice ? newPlanPrice?.interval : newPlan?.interval;

  return (
    <>
      <Grid container>
        <SectionTitleAndDescription
          title="Current plan"
          description={`
          ${userSubscription.plan.name} (${formatToUSD(
            userSubscription.amount,
            client.currencyIso,
          )} ${getBillingIntervalType(
            userSubscription.intervalType,
            userSubscription.chargeInterval,
          )})
            `}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
        <SectionTitleAndDescription
          title="New plan"
          description={`
              ${newPlan.name} (${formatToUSD(
                amount,
                client.currencyIso,
              )} ${getBillingIntervalType(
                intervalType,
                interval,
              )})
            `}
        />
      </Grid>
      <Divider vPadding={16} />
      <Grid container>
        <Grid item xs={12}>
          <Text
            variant="heading-5"
            color="wolf-800"
            weight="600"
            className="pb-3"
          >
            Add a discount
          </Text>
          <DiscountController
            control={control}
            category="PLAN"
            hideLabel
            isClearable
            label="Discount"
            name="discount"
            helperText="If the current plan has an active discount, we’ll pref-fill it in the above field."
          />
          {discount && discount.duration > 0 && (
            <div className="mt-3 mb-3">
              <Toast
                icon={Icons.Info}
                iconColor="success"
                message={
                  <>
                    This will be added as a <b>new discount</b>, and it will
                    apply for <b>{discount.duration} billing cycles</b>.
                  </>
                }
              />
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Divider vPadding={16} />
      </Grid>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Button buttonType="text" text="Back" onClick={goBack} />
        </Grid>
        <Grid item>
          <Button buttonType="secondary" text="Next" onClick={goToNextStep} />
        </Grid>
      </Grid>
    </>
  );
}

export default SelectDiscountStep;
